import FilterAlt from '@mui/icons-material/FilterAlt';
import FilterList from '@mui/icons-material/FilterList';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import { Theme, useTheme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import {
  DataGridPremium,
  DataGridPremiumProps,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
  GridFeatureMode,
  gridFilteredSortedRowEntriesSelector,
  gridFilteredSortedRowIdsSelector,
  GridRowId,
  GridRowSelectionModel,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { t } from 'i18next';
import { assignIn, cloneDeep, get, omit, uniqBy, unset } from 'lodash-es';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as UUID } from 'uuid';

import DataGridBottomBar from '~components/DataGrid/DataGridBottomBar';
import { DataGridToolBar } from '~components/DataGrid/DataGridToolbar';
import { NoDataFound } from '~components/DataGrid/NoDataFound';
import { DATAGRID_ID_PREFIX } from '~components/DataTable/dataTableStorageKeys';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';

type DataGridRowModel = GridValidRowModel;
type DataGridPremiumRowProps = DataGridPremiumProps<DataGridRowModel>;

// Shared default so an omitted `initialState` or `overrideInitialState` prop
// keeps one identity across renders; a fresh `{}` per render would re-run the
// stored-state restore below on every parent update.
const EMPTY_GRID_STATE: Record<string, unknown> = {};

interface DataGridProps {
  autoHeight?: boolean;
  bottomBarProps?: { hideClearSelectionButton?: boolean; elements?: ReactNode };
  columnVisibilityModel?: { [key: string]: boolean };
  columns: GridColDef[];
  detailPanelExpandedRowIds?: GridRowId[];
  disableColumnFilter?: boolean;
  filterMode?: GridFeatureMode;
  getDetailPanelContent?: DataGridPremiumRowProps['getDetailPanelContent'];
  getDetailPanelHeight?: DataGridPremiumRowProps['getDetailPanelHeight'];
  getRowId?: (row: DataGridRowModel) => string;
  headerSidebarComponent?: Nullable<ReactNode>;
  headerTrailingComponent?: Nullable<ReactNode>;
  hiddenColumns?: Array<string>;
  hideExport?: boolean;
  hideQuickFilter?: boolean;
  hideToolbar?: boolean;
  id: string; // Is using for save state to localstorage and restore it
  initialQuickFilterValue?: string;
  initialState?: Record<string, unknown>;
  loading?: boolean;
  noRowsOverlayAction?: ReactNode;
  noRowsOverlayText?: string;
  onChangeFilter?: (searchValue: string) => void;
  onDetailPanelExpandedRowIdsChange?: (newExpandedRowIds: GridRowId[]) => void;
  onRowClick?: DataGridPremiumRowProps['onRowClick'];
  onSelectedRowIDsChange?: (rowIDs: string[]) => void;
  overrideInitialState?: Record<string, unknown>; // This is used to override any saved state changes to the grid
  rows: DataGridRowModel[];
  selectedRowIDs?: string[];
  sx?: SxProps<Theme>;
}

type DataGridTableModel = {
  model: GridValidRowModel;
};

export type DataGridTableAPI = {
  gridFilteredSortedRowEntries: () => DataGridTableModel[];
  updateColumns: (columns: GridColDef[]) => void;
};

const ACTIONS_FIELD = 'actions';

const isActionsColumn = ({ field, type }: GridColDef) =>
  field === ACTIONS_FIELD || type === 'actions';

const DataGridTable = React.forwardRef<DataGridTableAPI, DataGridProps>(
  (props, outterRef) => {
    const {
      rows,
      columns,
      headerSidebarComponent,
      headerTrailingComponent,
      id,
      loading,
      sx,
      initialState = EMPTY_GRID_STATE,
      overrideInitialState = EMPTY_GRID_STATE,
      getRowId = (row) => (row.id as string) || UUID(),
      getDetailPanelContent,
      getDetailPanelHeight = () => 'auto' as const,
      detailPanelExpandedRowIds,
      onDetailPanelExpandedRowIdsChange,
      onChangeFilter,
      disableColumnFilter = false,
      hideQuickFilter = false,
      hideToolbar = false,
      filterMode = 'client',
      onRowClick,
      columnVisibilityModel,
      autoHeight = true,
      selectedRowIDs,
      onSelectedRowIDsChange,
      bottomBarProps,
      initialQuickFilterValue,
      noRowsOverlayAction,
      noRowsOverlayText,
    } = props;
    const apiRef = useGridApiRef();
    const theme = useTheme();
    const { toasterStore } = useStores();
    const gridId = useMemo(() => `${DATAGRID_ID_PREFIX}${id}`, [id]);

    const hasRowSelectionModel = Boolean(selectedRowIDs);
    const hasDetailPanel = Boolean(getDetailPanelContent);

    // Stabilize the rows handed to DataGridPremium across an in-flight fetch.
    //
    // Server-searched grids (Sites, Orders, Projects, etc.) replace `rows`
    // wholesale on every response. Under fast typing, a new rows array can land
    // while the grid is mid-render against the previous one, leaving the
    // virtualizer holding a rowId that no longer resolves to a node. MUI's
    // community `useGridRowAriaAttributes` then dereferences a null rowNode and
    // throws "Cannot read properties of null (reading 'id')" (HORIZON-FE-2R0).
    //
    // Holding the last-committed rows until `loading` flips back to false closes
    // that window: the prior rows stay mounted under the built-in loading overlay
    // and only swap once the fetch settles. Consumers that don't pass `loading`
    // commit rows immediately, so non-searching grids are unaffected.
    const isFetchInFlight = loading === true;
    const lastCommittedRowsRef = useRef(rows);
    if (!isFetchInFlight) {
      lastCommittedRowsRef.current = rows;
    }
    const committedRows = lastCommittedRowsRef.current;

    useEffect(() => {
      const stored = JSON.parse(localStorage.getItem(gridId) || '{}');

      // Set quickFilterValues to initial value if provided, otherwise clear it
      if (initialQuickFilterValue) {
        // Ensure the filter structure exists
        if (!stored.filter) stored.filter = {};
        if (!stored.filter.filterModel) stored.filter.filterModel = {};
        stored.filter.filterModel.quickFilterValues = [initialQuickFilterValue];
      } else {
        // Remove quickFilterValues(search bar values) from store on component mount
        unset(stored, 'filter.filterModel.quickFilterValues');
      }
      localStorage.setItem(gridId, JSON.stringify(stored));
    }, []);

    // v8 selection models carry a Set of ids plus a type: header select-all emits
    // an 'exclude' model listing only the deselected rows, so resolve it against
    // the filtered row set to keep handing consumers concrete row IDs.
    const onRowSelectionModelChange = React.useCallback(
      (rowSelectionModel: GridRowSelectionModel) => {
        if (rowSelectionModel.type === 'include') {
          onSelectedRowIDsChange?.(Array.from(rowSelectionModel.ids, String));
          return;
        }

        const filteredRowIds = gridFilteredSortedRowIdsSelector(apiRef);
        const selectedRowIds = filteredRowIds.filter(
          (rowId) => !rowSelectionModel.ids.has(rowId),
        );
        onSelectedRowIDsChange?.(selectedRowIds.map(String));
      },
      [apiRef, onSelectedRowIDsChange],
    );

    const rowSelectionModel = useMemo<GridRowSelectionModel | undefined>(
      () =>
        selectedRowIDs
          ? { type: 'include', ids: new Set<GridRowId>(selectedRowIDs) }
          : undefined,
      [selectedRowIDs],
    );

    const detailPanelExpandedRowIdsSet = useMemo(
      () => (detailPanelExpandedRowIds ? new Set(detailPanelExpandedRowIds) : undefined),
      [detailPanelExpandedRowIds],
    );

    const columnMemorized = useMemo(() => {
      const columnMemorized = columns.map((item) => ({
        aggregable: false,
        groupable: false,
        ...item,
        ...(isActionsColumn(item) ? { pinnable: false, resizable: false } : {}),
      }));

      if (hasRowSelectionModel) {
        columnMemorized.unshift({
          ...GRID_CHECKBOX_SELECTION_COL_DEF,
          aggregable: false,
          groupable: false,
          sortable: false,
          disableReorder: true,
        });
      }

      if (hasDetailPanel) {
        columnMemorized.unshift({
          ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
          aggregable: false,
          groupable: false,
          sortable: false,
        });
      }

      return columnMemorized;
    }, [columns, hasRowSelectionModel, hasDetailPanel]);

    React.useImperativeHandle(outterRef, () => ({
      gridFilteredSortedRowEntries: () => {
        const paginationModel = gridFilteredSortedRowEntriesSelector(apiRef);
        return paginationModel;
      },
      updateColumns: (columns: GridColDef[]) => {
        apiRef.current?.updateColumns(columns);
      },
    }));

    const [expandedGroupRows, setExpandedGroupRows] = useState<Array<GridRowId>>([]);

    const errorHandler = () => {
      toasterStore.push(
        alert(`${t('form_validation_errors.unable_update_row')}`, AlertTypes.error),
      );
    };

    const updateGridStateLocallyStored = (model: unknown) => {
      const state = apiRef.current?.exportState();

      if (!state) {
        return;
      }

      // Pagination and preferencePanel is not needed to store
      const toStore = omit(state, ['preferencePanel', 'pagination']);

      localStorage.setItem(gridId, JSON.stringify(toStore));
      const quickFilterValuesModel = (model as { quickFilterValues?: unknown })
        ?.quickFilterValues;

      if (onChangeFilter && Array.isArray(quickFilterValuesModel)) {
        const quickFilterValues = quickFilterValuesModel.map(String).join(' ');

        if (quickFilterValues.length > 2 || quickFilterValues.length === 0) {
          onChangeFilter(quickFilterValues);
        }
      }
    };

    const lastRestoredGridIdRef = useRef<string | null>(null);

    // Init(first render) state from localstorage
    useEffect(() => {
      const stored = JSON.parse(localStorage.getItem(gridId) || '{}');
      const value = assignIn(
        cloneDeep(initialState),
        cloneDeep(stored),
        cloneDeep(overrideInitialState),
      );

      if (hasRowSelectionModel && value?.columns?.orderedFields) {
        const checksFieldIndex = value.columns.orderedFields.findIndex(
          (fieldName: string) => {
            return fieldName === GRID_CHECKBOX_SELECTION_COL_DEF.field;
          },
        );

        if (checksFieldIndex > -1) {
          value.columns.orderedFields.splice(checksFieldIndex, 1);
        }

        value.columns.orderedFields.splice(
          hasDetailPanel ? 1 : 0,
          0,
          GRID_CHECKBOX_SELECTION_COL_DEF.field,
        );
      }

      // Merge filter items:
      // Only existing should be re-written by external(initialState) values
      // Quick filter should be the same
      const initialFilterItems = get(
        initialState,
        'filter.filterModel.items',
        [],
      ) as Array<Record<string, unknown>>;
      const storedFilterItems = get(stored, 'filter.filterModel.items', []) as Array<
        Record<string, unknown>
      >;
      const filterItems = uniqBy([...initialFilterItems, ...storedFilterItems], 'field');

      value.filter = {
        ...stored.filter,
        filterModel: {
          ...stored.filter?.filterModel,
          items: filterItems,
        },
      };

      // Heals stored state saved before action columns were locked to the right.
      const actionsFields = columns.filter(isActionsColumn).map(({ field }) => field);

      if (actionsFields.length) {
        const withoutActions = (side: string) => {
          const pinned = get(value, `pinnedColumns.${side}`, []) as unknown;

          return (Array.isArray(pinned) ? (pinned as Array<string>) : []).filter(
            (field) => !actionsFields.includes(field),
          );
        };

        value.pinnedColumns = {
          left: withoutActions('left'),
          right: [...withoutActions('right'), ...actionsFields],
        };

        actionsFields.forEach((field) => unset(value, ['columns', 'dimensions', field]));
      }

      // Only the first restore may carry `sorting`. MUI publishes
      // `sortModelChange` for any new sort-model array, even an identical one,
      // and its pagination hook scrolls the grid to the top on that event — so
      // re-applying the stored sort on a column update would throw away the
      // user's scroll position.
      if (lastRestoredGridIdRef.current === gridId) {
        delete value.sorting;
      }
      lastRestoredGridIdRef.current = gridId;

      apiRef?.current?.restoreState(value);
      // Most of our data grids are updateing the column details based on the row data
      // The column updates that happen after the intial loads will just set the columns to their intial state
      // So the stored column states should be applied after the columns have been updated to fix this issue
    }, [
      gridId,
      initialState,
      columns,
      columnMemorized,
      hasRowSelectionModel,
      hasDetailPanel,
    ]);

    useEffect(() => {
      apiRef.current?.subscribeEvent('rowExpansionChange', (params) => {
        const { childrenExpanded, id } = params;

        setExpandedGroupRows((prev) => {
          if (childrenExpanded) {
            return [...new Set([...prev, id])];
          } else {
            return prev.filter((item) => item !== id);
          }
        });
      });
    }, [apiRef]);

    useEffect(() => {
      expandedGroupRows.forEach((groupId) => {
        apiRef.current?.setRowChildrenExpansion(groupId, true);
      });
    }, [columns]);

    return (
      <>
        <Box
          sx={{
            display: 'block',
            ...sx,
          }}
        >
          <DataGridPremium
            key={id}
            apiRef={apiRef}
            loading={loading}
            rows={committedRows}
            autoHeight={autoHeight}
            columns={columnMemorized}
            getRowId={getRowId}
            onProcessRowUpdateError={errorHandler}
            sx={{
              border: `1px  solid ${theme.palette.divider}`,
              pt: hideToolbar ? 0 : 2,
              pb: 2,
              // Dark mode: lift the grid onto a raised surface (rows sit above the page
              // base) with a faint top-edge highlight + soft shadow — mirrors the card
              // elevation so tables read as raised panels rather than a flat transparent
              // region. A plain drop shadow alone is invisible on the near-black base.
              // Light mode is untouched (transparent body, shipped header value).
              ...(theme.palette.mode === 'dark'
                ? {
                    backgroundColor: theme.semantic.surfaceRaised,
                    boxShadow:
                      'inset 0 1px 0 0 rgba(255, 255, 255, 0.06), 0 2px 8px 0 rgba(0, 0, 0, 0.45)',
                  }
                : {}),
              // treadGray10 keeps the light-mode header value pixel-identical; in dark the
              // header bar recesses to the sunken tone below the raised body — a distinct
              // header, consistent with the Approve Loads table.
              '--DataGrid-containerBackground':
                theme.palette.mode === 'dark'
                  ? theme.semantic.surfaceSunken
                  : theme.brandV2.colors.treadGray10,
              '& .MuiDataGrid-overlayWrapperInner, .MuiDataGrid-virtualScroller': {
                minHeight: '96px',
              },
              '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell, .MuiDataGrid-columnHeader, .MuiDataGrid-virtualScroller':
                { borderBottom: `1px solid ${theme.palette.divider}` },
              // v8's toolbar container paints a 1px bottom border and a 4px bottom
              // padding that v7 did not have (v7 put those 4px inside the quick
              // filter's own padding — restored in DataGridToolbar); removing both
              // keeps the grid content at its v7 offset.
              '& .MuiDataGrid-toolbarContainer': {
                px: 2,
                pb: 0,
                borderBottom: 'none',
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              "& .MuiDataGrid-columnHeader[data-field='__check__'], .MuiDataGrid-cellCheckbox[data-field='__check__']":
                {
                  minWidth: '42px !important',
                  maxWidth: '42px !important',
                  width: '42px !important',
                },
            }}
            slotProps={{
              filterPanel: {
                sx: {
                  py: 2,
                  pl: 2,
                  pr: 0,
                  '& .MuiDataGrid-panelFooter': { mt: 1, ml: 0.5, mr: 2.5 },
                },
              },
              columnsPanel: { sx: { '& .MuiDataGrid-panelFooter': { mx: 0.5 } } },
              baseSelect: {
                native: true,
                size: 'small',
                material: { sx: { mr: 2 } },
              },
              baseTextField: { size: 'small', material: { sx: { mr: 2 } } },
            }}
            slots={{
              noRowsOverlay: () => (
                <NoDataFound
                  noDataText={noRowsOverlayText}
                  action={noRowsOverlayAction}
                />
              ),
              noResultsOverlay: () => <NoDataFound />,
              columnFilteredIcon: () => {
                return (
                  <FilterAlt sx={{ fill: theme.palette.primary.main, height: 18 }} />
                );
              },
              openFilterButtonIcon: () => {
                return (
                  <FilterList sx={{ fill: theme.palette.secondary.main, height: 18 }} />
                );
              },
              detailPanelCollapseIcon: () => {
                return (
                  <KeyboardArrowDown
                    sx={{ fill: theme.palette.text.secondary, height: 18, width: 18 }}
                  />
                );
              },
              detailPanelExpandIcon: () => {
                return (
                  <KeyboardArrowRight
                    sx={{ fill: theme.palette.text.secondary, height: 18, width: 18 }}
                  />
                );
              },
              toolbar: () => {
                return (
                  <DataGridToolBar
                    toolbarTrailingContent={headerTrailingComponent}
                    toolbarSidebarContent={headerSidebarComponent}
                    hideQuickFilter={hideQuickFilter}
                  />
                );
              },
            }}
            onRowGroupingModelChange={updateGridStateLocallyStored}
            onDetailPanelExpandedRowIdsChange={
              onDetailPanelExpandedRowIdsChange
                ? (expandedRowIds: Set<GridRowId>) =>
                    onDetailPanelExpandedRowIdsChange(Array.from(expandedRowIds))
                : updateGridStateLocallyStored
            }
            detailPanelExpandedRowIds={detailPanelExpandedRowIdsSet}
            onFilterModelChange={updateGridStateLocallyStored}
            onColumnOrderChange={updateGridStateLocallyStored}
            onPinnedColumnsChange={updateGridStateLocallyStored}
            onColumnResize={updateGridStateLocallyStored}
            onColumnVisibilityModelChange={updateGridStateLocallyStored}
            onSortModelChange={updateGridStateLocallyStored}
            initialState={{}}
            disableRowSelectionOnClick
            hideFooter={true}
            hideFooterPagination={true}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            defaultGroupingExpansionDepth={0}
            hideFooterRowCount={committedRows.length <= 100}
            disableColumnFilter={disableColumnFilter}
            filterMode={filterMode}
            onRowClick={onRowClick}
            columnVisibilityModel={columnVisibilityModel}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={onRowSelectionModelChange}
            checkboxSelection={hasRowSelectionModel}
            showToolbar={!hideToolbar}
          />
        </Box>
        {hasRowSelectionModel && (
          <DataGridBottomBar
            selectedRowIDs={selectedRowIDs ?? []}
            onClearSelectedRows={
              bottomBarProps?.hideClearSelectionButton
                ? undefined
                : () => onRowSelectionModelChange({ type: 'include', ids: new Set() })
            }
            elements={bottomBarProps?.elements}
          />
        )}
      </>
    );
  },
);

DataGridTable.displayName = 'DataGridTable';

export default DataGridTable;
